import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { Moon, Sun, ShieldCheck, Sparkles, AlertTriangle, Loader2 } from "lucide-react";

export default function Settings() {
  const { user } = useAuth();
  const [dark, setDark] = useState(() => localStorage.getItem("reshine_theme") === "dark");
  const [purgeText, setPurgeText] = useState("");
  const [purging, setPurging] = useState(false);

  const runPurge = async () => {
    if (purgeText !== "PURGE") return;
    setPurging(true);
    try {
      const res = await api.post("/admin/purge-data", { confirm: "PURGE" });
      const d = res.data.deleted;
      toast.success(`Clean slate done — deleted ${d.jobs} jobs, ${d.customers} customers, ${d.photos} photos, ${d.notes} notes, ${d.status_history} history entries. Job counter reset to RI-2026-00001.`);
      setPurgeText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purge failed");
    } finally {
      setPurging(false);
    }
  };

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("reshine_theme", dark ? "dark" : "light");
  }, [dark]);

  return (
    <div className="max-w-xl" data-testid="settings-page">
      <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Settings</h1>

      <Card className="mt-4">
        <CardContent className="p-5 flex items-center gap-4">
          <Avatar className="h-14 w-14">
            <AvatarImage src={user?.picture} />
            <AvatarFallback className="bg-accent text-accent-foreground font-semibold">{(user?.name || "U")[0]}</AvatarFallback>
          </Avatar>
          <div>
            <p className="font-semibold" data-testid="settings-user-name">{user?.name}</p>
            <p className="text-sm text-muted-foreground" data-testid="settings-user-email">{user?.email}</p>
            <span className="status-pill bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950/60 dark:text-amber-300 dark:border-amber-900 mt-1" data-testid="settings-user-role">{user?.role}</span>
          </div>
        </CardContent>
      </Card>

      <Card className="mt-4">
        <CardContent className="p-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {dark ? <Moon className="h-5 w-5 text-primary" /> : <Sun className="h-5 w-5 text-primary" />}
            <div>
              <p className="text-sm font-semibold">Appearance</p>
              <p className="text-xs text-muted-foreground">{dark ? "Dark (Obsidian)" : "Light (Linen)"} theme</p>
            </div>
          </div>
          <Button variant="outline" onClick={() => setDark(!dark)} data-testid="theme-toggle-button">
            Switch to {dark ? "Light" : "Dark"}
          </Button>
        </CardContent>
      </Card>

      <Card className="mt-4">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            <p className="text-sm font-semibold">Security</p>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            You are signed in with Google. Sessions expire automatically after 7 days. Customer data and photographs are private and accessible only to authorized Reshine India staff.
          </p>
        </CardContent>
      </Card>

      {user?.role === "admin" && (
        <Card className="mt-4 border-red-300 dark:border-red-900" data-testid="danger-zone">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              <p className="text-sm font-semibold text-red-700 dark:text-red-400">Danger Zone</p>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed mb-3">
              Permanently erase ALL jobs, customers, photos, notes and history, and reset the job counter so the next job is RI-2026-00001. Staff accounts are kept. This cannot be undone.
            </p>
            <div className="flex gap-2">
              <Input
                data-testid="purge-confirm-input"
                value={purgeText}
                onChange={(e) => setPurgeText(e.target.value)}
                placeholder='Type PURGE to confirm'
                className="h-11"
              />
              <Button
                variant="destructive"
                data-testid="purge-data-button"
                disabled={purgeText !== "PURGE" || purging}
                onClick={runPurge}
                className="shrink-0"
              >
                {purging ? <Loader2 className="h-4 w-4 animate-spin" /> : "Erase All Data"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <p className="text-xs text-muted-foreground mt-6 flex items-center gap-1.5">
        <Sparkles className="h-3.5 w-3.5 text-primary" /> Reshine India — Metal Restoration & Refinishing Operations
      </p>
    </div>
  );
}
